import { useCallback, useLayoutEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { LIST_ITEM } from "../data/noteDragTypes";

// ─── FLIP Animation Hook ────────────────────────────────────
// Identical pattern to customNotes/NotePreviewColumn.js
function useFlipAnimation(containerRef) {
  const positionsRef = useRef({});

  const capturePositions = useCallback(() => {
    if (!containerRef.current) return;
    const children = containerRef.current.querySelectorAll("[data-item-id]");
    const positions = {};
    children.forEach((el) => {
      positions[el.dataset.itemId] = el.getBoundingClientRect();
    });
    positionsRef.current = positions;
  }, [containerRef]);

  const animatePositions = useCallback(() => {
    if (!containerRef.current) return;
    const oldPositions = positionsRef.current;
    if (!Object.keys(oldPositions).length) return;

    const children = containerRef.current.querySelectorAll("[data-item-id]");
    children.forEach((el) => {
      const id = el.dataset.itemId;
      const oldRect = oldPositions[id];
      if (!oldRect) return;

      const newRect = el.getBoundingClientRect();
      const deltaY = oldRect.top - newRect.top;
      if (Math.abs(deltaY) < 1) return;

      el.style.transform = `translateY(${deltaY}px)`;
      el.style.transition = "none";

      requestAnimationFrame(() => {
        el.style.transition = "transform 250ms cubic-bezier(0.2, 0, 0, 1)";
        el.style.transform = "";
        el.addEventListener(
          "transitionend",
          () => {
            el.style.transition = "";
            el.style.transform = "";
          },
          { once: true }
        );
      });
    });

    positionsRef.current = {};
  }, [containerRef]);

  return { capturePositions, animatePositions };
}

/**
 * useAnimatedDragList – manages FLIP animation for a react-dnd sortable list.
 * Trigger: whenever `items` changes, animatePositions runs in useLayoutEffect.
 *
 * @param {Array}          items         The current list (triggers animation on change)
 * @param {React.RefObject} containerRef Ref to the wrapping DOM element
 * @returns {{ capturePositions: Function }}
 */
export function useAnimatedDragList(items, containerRef) {
  const { capturePositions, animatePositions } = useFlipAnimation(containerRef);

  useLayoutEffect(() => {
    animatePositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return { capturePositions };
}

/**
 * DraggableRow – generic wrapper that makes any list item draggable via react-dnd.
 * Uses render-prop children: children({ dragRef, isDragging })
 *
 * @param {Object} props
 * @param {string}   props.itemId       Stable unique ID for this item
 * @param {number}   props.index        Current array index
 * @param {Function} props.onMove       (fromIndex, toIndex) => void
 * @param {Function} props.onBeforeMove () => void  (call capturePositions)
 * @param {Function} [props.onDrop]     () => void  (called once on drop – fire onReorder here)
 * @param {boolean}  [props.disabled]   Disable drag when not editable
 * @param {string}   [props.className]  CSS class for the row
 * @param {Function} props.children     Render prop: ({ dragRef, isDragging }) => JSX
 */
export function DraggableRow({ itemId, index, onMove, onBeforeMove, onDrop, disabled, className, children }) {
  const ref = useRef(null);

  const [{ isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: LIST_ITEM,
      item: () => ({ id: itemId, index }),
      canDrag: () => !disabled,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      end: (_item, monitor) => {
        if (monitor.didDrop() && onDrop) onDrop();
      },
    }),
    [itemId, index, disabled, onDrop]
  );

  const [, dropRef] = useDrop(
    () => ({
      accept: LIST_ITEM,
      hover: (dragItem, monitor) => {
        if (!ref.current) return;
        const dragIndex = dragItem.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const hoverRect = ref.current.getBoundingClientRect();
        const hoverMidY = (hoverRect.bottom - hoverRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const hoverClientY = clientOffset.y - hoverRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMidY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMidY) return;

        onBeforeMove();
        onMove(dragIndex, hoverIndex);
        dragItem.index = hoverIndex;
      },
    }),
    [index, onMove, onBeforeMove]
  );

  const combinedRef = useCallback(
    (el) => {
      ref.current = el;
      dropRef(el);
      previewRef(el);
    },
    [dropRef, previewRef]
  );

  const itemClassName = [className, isDragging ? "dragging" : ""].filter(Boolean).join(" ");

  return (
    <div ref={combinedRef} data-item-id={itemId} className={itemClassName}>
      {typeof children === "function" ? children({ dragRef, isDragging }) : children}
    </div>
  );
}
